import {
    useCreateEnvironment,
    useCreateEnvironmentFromProject,
    useDeleteEnvironment,
} from "@/queries/environment-queries";
import type { ProjectContext, SelectedContext } from "@/components/ai/prompt-input/NewPromptInput";
import { useCallback, useRef } from "react";
import type { EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { useCreateAgentExecution, useSendToAgentExecution } from "@/queries/agent-queries";
import { useAgentId } from "@/hooks/use-agent-id";
import {
    AgentCodeContext,
    SendToAgentExecutionRequest,
    UserInputBlock,
    UserInputBlock_TextInput,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import type { Environment } from "gitpod-next-api/gitpod/v1/environment_pb";
import { EnvironmentSpec_DevContainer } from "gitpod-next-api/gitpod/v1/environment_pb";
import type { PlainMessage } from "@bufbuild/protobuf";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { parseAsBoolean, useQueryState } from "nuqs";
import { useNavigate } from "react-router-dom";
import { getDetailsURL } from "@/routes/environments/details-url";
import { formatError, isVerifyCardError } from "@/utils/errors";
import { useGetOrganizationPolicies } from "@/queries/organization-queries";
import { useSegmentTrackError } from "@/hooks/use-segment";
import { v4 as uuidv4 } from "uuid";

// Universal devcontainer image that is pre-pulled on Ona runners
// NOTE: This should be kept in sync with the image we pre-pull in the rootfs in runner/rootfs/devcontainer-seed-images.txt
const UNIVERSAL_DEVCONTAINER_IMAGE = "mcr.microsoft.com/devcontainers/universal:4.0.1-noble";

export function useSubmitPrompts() {
    const createEnvironment = useCreateEnvironment();
    const createEnvironmentFromProject = useCreateEnvironmentFromProject();
    const startAgentExecution = useCreateAgentExecution();
    const agentId = useAgentId();
    const sendToAgentExecution = useSendToAgentExecution();
    const deleteEnvironment = useDeleteEnvironment();
    const { toast } = useToast();
    const navigate = useNavigate();
    const [, setOnboardingForceHideSidebar] = useQueryState<boolean>(
        "ona-onboarding",
        parseAsBoolean.withDefault(false),
    );
    const { data: organizationPolicies } = useGetOrganizationPolicies();
    const trackError = useSegmentTrackError();

    // Helper function to get devcontainer config for Ona environments
    const getDevcontainerConfig = useCallback(() => {
        // Only set universal image if no org policy exists for default environment image
        if (!organizationPolicies?.defaultEnvironmentImage) {
            return new EnvironmentSpec_DevContainer({
                defaultDevcontainerImage: UNIVERSAL_DEVCONTAINER_IMAGE,
            });
        }
        return undefined;
    }, [organizationPolicies?.defaultEnvironmentImage]);

    const createEnvironmentForContexts = useCallback(
        (contexts: SelectedContext[], envClass: EnvironmentClassEntry | null) => {
            let projectContext: ProjectContext | null = null;
            let commitHashFound = "";
            let branchNameFound = "";
            let contextUrl = "";
            for (const ctx of contexts) {
                switch (ctx.kind) {
                    case "project":
                        projectContext = ctx;
                        break;
                    case "issue":
                        branchNameFound = ctx.branchName;
                        contextUrl = ctx.url;
                        break;
                    case "pr":
                        branchNameFound = ctx.fromBranchName;
                        contextUrl = ctx.url;
                        break;
                    case "git-commit":
                        commitHashFound = ctx.commitHash;
                        contextUrl = ctx.url;
                        break;
                    case "repo":
                        contextUrl = ctx.url;
                        break;
                    case "scratch":
                        contextUrl = "";
                        break;
                }
            }

            const devcontainerConfig = getDevcontainerConfig();

            if (projectContext) {
                if (commitHashFound) {
                    return createEnvironmentFromProject.mutateAsync({
                        project: projectContext.project,
                        gitInitializerParams: {
                            ref: commitHashFound,
                            refType: "commit",
                        },
                        devcontainerConfig,
                    });
                }
                if (branchNameFound) {
                    return createEnvironmentFromProject.mutateAsync({
                        project: projectContext.project,
                        gitInitializerParams: {
                            ref: branchNameFound,
                            refType: "branch",
                        },
                        devcontainerConfig,
                    });
                }
                return createEnvironmentFromProject.mutateAsync({
                    project: projectContext.project,
                    devcontainerConfig,
                });
            }

            if (!envClass) {
                throw new Error("environment class must be selected for non-project context!");
            }

            if (contextUrl) {
                return createEnvironment.mutateAsync({
                    type: "contextUrl",
                    contextURL: contextUrl,
                    classID: envClass.clazz.id,
                    devcontainerConfig,
                });
            }
            return createEnvironment.mutateAsync({
                type: "blank",
                classID: envClass.clazz.id,
                devcontainerConfig,
            });
        },
        [createEnvironment, createEnvironmentFromProject, getDevcontainerConfig],
    );

    const sendPromptToAgentExecution = useCallback(
        async (prompt: string, environment: PlainMessage<Environment>, templateTitle?: string) => {
            const { agentExecutionId } = await startAgentExecution.mutateAsync({
                agentId,
                codeContext: new AgentCodeContext({
                    context: {
                        case: "environmentId",
                        value: environment.id,
                    },
                }),
                name: templateTitle,
            });
            await sendToAgentExecution.mutateAsync(
                new SendToAgentExecutionRequest({
                    agentExecutionId,
                    userInput: new UserInputBlock({
                        id: uuidv4(),
                        input: {
                            case: "text",
                            value: new UserInputBlock_TextInput({
                                content: prompt,
                            }),
                        },
                    }),
                }),
            );
        },
        [agentId, sendToAgentExecution, startAgentExecution],
    );

    const cleanupEnvironment = useCallback(
        async (env: PlainMessage<Environment>, originalError: unknown) => {
            try {
                await deleteEnvironment.mutateAsync({
                    environmentId: env.id,
                    force: false,
                });
            } catch (error) {
                console.error("Environment cleanup failed after conversation failure", {
                    environmentId: env.id,
                    agentId,
                    originalError,
                    cleanupError: error,
                });
            }
        },
        [deleteEnvironment, agentId],
    );

    const submitPrompt = useCallback(
        async (
            prompt: string,
            contexts: SelectedContext[],
            envClass: EnvironmentClassEntry | null,
            templateTitle?: string,
        ) => {
            let environment: PlainMessage<Environment> | null = null;
            try {
                environment = await createEnvironmentForContexts(contexts, envClass);
                await sendPromptToAgentExecution(prompt, environment, templateTitle);
                return environment;
            } catch (error) {
                if (environment) {
                    // silently delete created environment in the background
                    void cleanupEnvironment(environment, error);
                }
                throw error;
            }
        },
        [createEnvironmentForContexts, cleanupEnvironment, sendPromptToAgentExecution],
    );

    // Using a ref to track the duplicate submissions so that it's persisted between callbacks.
    // E.g. for rapid submissions the submit callback would be called twice before the re-renders would
    // produce an updated callback with an early return.
    const isSubmittingRef = useRef(false);
    const submit = useCallback(
        async ({
            prompts,
            contexts,
            environmentClass,
            shouldNavigate = true,
            templateTitles,
        }: {
            prompts: string[];
            contexts: SelectedContext[];
            environmentClass: EnvironmentClassEntry | null;
            shouldNavigate?: boolean;
            templateTitles?: string[];
        }) => {
            if (isSubmittingRef.current) {
                console.warn("duplicate prompt submission - ignoring");
                return;
            }
            try {
                isSubmittingRef.current = true;
                const results = await Promise.allSettled(
                    prompts.map((prompt, index) => {
                        const templateTitle = templateTitles?.[index];
                        return submitPrompt(prompt, contexts, environmentClass, templateTitle);
                    }),
                );
                const successCount = results.reduce(
                    (count, result) => (result.status === "fulfilled" ? count + 1 : count),
                    0,
                );
                const failureCount = results.length - successCount;
                const isCompleteFailure = failureCount === results.length;

                // Get the first successful environment for navigation
                let firstSuccessfulEnvironment: PlainMessage<Environment> | null = null;
                for (const result of results) {
                    switch (result.status) {
                        case "fulfilled":
                            if (result.value) {
                                firstSuccessfulEnvironment = result.value;
                            }
                            break;
                        case "rejected":
                            trackError("prompt_submission_error", result.reason);
                            break;
                    }
                }

                if (successCount > 0) {
                    // Only show toast if we're not navigating to the conversation
                    // When navigating, the user will see the conversation directly
                    if (!shouldNavigate || !firstSuccessfulEnvironment) {
                        toast({
                            title: "Conversations started",
                            description: `Successfully started ${successCount} execution${successCount > 1 ? "s" : ""}${failureCount > 0 ? `. ${failureCount} failed.` : ""}`,
                        });
                    }

                    // Navigate to the first successful environment
                    if (firstSuccessfulEnvironment && shouldNavigate) {
                        navigate(getDetailsURL({ environment: firstSuccessfulEnvironment }));
                    }
                }
                if (failureCount === results.length) {
                    // Get the first error to show a more specific message
                    let errorMessage = "Could not start any conversations. Please try again.";
                    let error = new Error(errorMessage);
                    for (const result of results) {
                        if (result.status === "rejected") {
                            errorMessage = formatError(result.reason);
                            error = result.reason as Error;
                            break;
                        }
                    }

                    // In this case, the error is handled by the VerifyCardModal
                    // and a toast would be redundant
                    if (!isVerifyCardError(error)) {
                        toast({
                            title: "Failed to start executions",
                            description: errorMessage,
                        });
                    }
                }
                await setOnboardingForceHideSidebar(false);
                return isCompleteFailure;
            } finally {
                isSubmittingRef.current = false;
            }
        },
        [setOnboardingForceHideSidebar, submitPrompt, trackError, toast, navigate],
    );

    return {
        submitPrompts: submit,
        isSubmittingPrompts:
            createEnvironment.isPending ||
            createEnvironmentFromProject.isPending ||
            startAgentExecution.isPending ||
            sendToAgentExecution.isPending,
    };
}
